// AssetIcon — small branded SVG glyphs for the 7 asset types.
// Brand language: ink (#1d1d1d) line on light surface, with selective accent
// red (#ff2b0a) hits to call out the "active" element of each shape.
// Each icon lives inside a 28x28 surface chip so the icons feel like the
// element-cell glyphs Mike uses everywhere else in the app.

import type { AssetType } from '../types';

const STROKE = 'currentColor';
const ACCENT = '#ff2b0a';

interface Props {
  type: AssetType;
  size?: number; // outer chip size
}

export function AssetIcon({ type, size = 28 }: Props) {
  return (
    <span
      className="shrink-0 inline-grid place-items-center bg-surface border border-hairline rounded-md text-ink"
      style={{ width: size, height: size }}
    >
      <svg
        width={size * 0.64}
        height={size * 0.64}
        viewBox="0 0 24 24"
        fill="none"
        stroke={STROKE}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {ICONS[type] ?? ICONS.brief}
      </svg>
    </span>
  );
}

const ICONS: Record<AssetType, JSX.Element> = {
  brief: (
    <>
      {/* Document with folded corner + text lines; accent stamp top-right */}
      <path d="M6 3 h9 l3 3 v15 a1 1 0 0 1 -1 1 H6 a1 1 0 0 1 -1 -1 V4 a1 1 0 0 1 1 -1 z" />
      <polyline points="15,3 15,6 18,6" />
      <line x1="8" y1="11" x2="15" y2="11" />
      <line x1="8" y1="14" x2="15" y2="14" />
      <line x1="8" y1="17" x2="12" y2="17" />
      <circle cx="16" cy="9" r="1.1" fill={ACCENT} stroke="none" />
    </>
  ),

  supply_map: (
    <>
      {/* Connected nodes — sourcing graph; central node in accent */}
      <line x1="5" y1="6" x2="11" y2="11" />
      <line x1="11" y1="11" x2="18" y2="6" />
      <line x1="11" y1="11" x2="18" y2="18" />
      <line x1="11" y1="11" x2="5" y2="18" />
      <circle cx="5" cy="6" r="1.7" fill="#fff" />
      <circle cx="18" cy="6" r="1.7" fill="#fff" />
      <circle cx="18" cy="18" r="1.7" fill="#fff" />
      <circle cx="5" cy="18" r="1.7" fill="#fff" />
      <circle cx="11" cy="11" r="2.2" fill={ACCENT} stroke="none" />
    </>
  ),

  timeline: (
    <>
      {/* Three stacked gantt bars; middle bar in accent */}
      <line x1="3" y1="4" x2="3" y2="20" strokeWidth="1.2" />
      <rect x="4.5" y="5.5" width="9" height="3" rx="0.6" fill={STROKE} stroke="none" />
      <rect x="7.5" y="10.5" width="11" height="3" rx="0.6" fill={ACCENT} stroke="none" />
      <rect x="5.5" y="15.5" width="8" height="3" rx="0.6" fill={STROKE} stroke="none" />
    </>
  ),

  workflow: (
    <>
      {/* Three connected step boxes with arrows; final arrowhead in accent */}
      <rect x="3" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="14" width="7" height="5" rx="1" />
      <line x1="10" y1="5.5" x2="14" y2="5.5" />
      <polyline points="13,4.5 14,5.5 13,6.5" />
      <line x1="17.5" y1="8" x2="17.5" y2="14" />
      <polyline points="16.5,13 17.5,14 18.5,13" stroke={ACCENT} />
    </>
  ),

  flowchart: (
    <>
      {/* Decision diamond with two branches; right branch in accent */}
      <rect x="8.5" y="2.5" width="7" height="4" rx="0.8" />
      <line x1="12" y1="6.5" x2="12" y2="9" />
      <polygon points="12,9 16,13 12,17 8,13" />
      <line x1="9" y1="15" x2="5.5" y2="20" />
      <line x1="15" y1="15" x2="18.5" y2="20" stroke={ACCENT} />
      <circle cx="5.5" cy="20.5" r="1.3" />
      <circle cx="18.5" cy="20.5" r="1.3" fill={ACCENT} stroke="none" />
    </>
  ),

  notes: (
    <>
      {/* Bullet list; first bullet in accent */}
      <circle cx="5" cy="6" r="1.3" fill={ACCENT} stroke="none" />
      <line x1="9" y1="6" x2="20" y2="6" />
      <circle cx="5" cy="12" r="1.3" fill={STROKE} />
      <line x1="9" y1="12" x2="20" y2="12" />
      <circle cx="5" cy="18" r="1.3" fill={STROKE} />
      <line x1="9" y1="18" x2="17" y2="18" />
    </>
  ),

  report: (
    <>
      {/* Bar chart on a baseline; tallest bar in accent */}
      <line x1="3" y1="20.5" x2="21" y2="20.5" />
      <rect x="5" y="14" width="3.2" height="6.5" rx="0.4" fill={STROKE} stroke="none" />
      <rect x="10.4" y="8" width="3.2" height="12.5" rx="0.4" fill={ACCENT} stroke="none" />
      <rect x="15.8" y="11" width="3.2" height="9.5" rx="0.4" fill={STROKE} stroke="none" />
    </>
  ),
};
